import moment from "moment";
import { CancellableAPIRequest, makeCancellableAPIRequest } from "restAPI";
import { AQLMetadataDTO } from "types/api/aqlMetadataDto";
import { AssetExportData } from "types/api/assetExportData";
import {
  AssetStatsFilterRequest,
  AssetStatsResponse,
} from "types/api/assetStatsDto";
import { AssetSummary } from "types/api/assetSummary";
import { assetTimeLineReqDto } from "types/api/assetTimeLineReqDto";
import { AssetsResDto } from "types/api/assetsResDto";
import { BaseResponseDto } from "types/api/baseResponseDto";
import { AssetEsDto } from "types/assetdetails";
import { CommentResponse } from "types/comment";
import { GroupStatsResponse } from "types/common";
import { PageResponse } from "types/pageResponse";
import { FilterInfo } from "types/pagination";
import { HistoryResponse } from "views/Inventory/Assets/AssetDetails/asset-history";

const AssetsAPI = {
  getAll(queryParams: object) {
    const modifiedParams = {
      ...queryParams,
      //@ts-expect-error
      sortColumns: queryParams.sortColumns?.map((sortObj) =>
        sortObj.property === "findingsBreakdown"
          ? { ...sortObj, property: "findingsCount" }
          : sortObj,
      ),
    };
    return makeCancellableAPIRequest<PageResponse<AssetSummary>>(
      "POST",
      "/api/v2/assets",
      {
        data: modifiedParams,
      },
    );
  },
  getFilterValues(data = {}) {
    return makeCancellableAPIRequest<Record<string, string[]>>(
      "POST",
      "/api/v2/assets/assets-filters",
      {
        data,
      },
    );
  },
  getAssetDetails(assetId: number) {
    return makeCancellableAPIRequest<AssetEsDto>(
      "GET",
      `/api/v2/assets/${assetId}`,
    );
  },
  updateTags(assetId: number, data: { tags: string[] }) {
    return makeCancellableAPIRequest("PUT", `/api/v2/assets/${assetId}/tags`, {
      data,
    });
  },
  updateAssetDetails(
    assetId: number,
    data: { active?: string | number; name?: string },
  ) {
    return makeCancellableAPIRequest("PUT", `/api/v2/assets/${assetId}`, {
      data,
    });
  },
  getGroupStats(data: {
    primaryField: string;
    secondaryFields?: string[];
    filters?: FilterInfo;
    keysToBeIncludedInPrimary?: string[];
    keysToBeIncludedInSecondary?: string[];
  }) {
    const path = "/api/v2/assets/groups/count";
    return makeCancellableAPIRequest<GroupStatsResponse>("POST", path, {
      data,
    });
  },
  getHistory(assetID: number) {
    return makeCancellableAPIRequest<PageResponse<HistoryResponse>>(
      "GET",
      `/api/v2/assets/${assetID}/history`,
    );
  },
  getComments(assetID: number) {
    return makeCancellableAPIRequest<PageResponse<CommentResponse>>(
      "GET",
      `/api/v2/assets/${assetID}/comments`,
    );
  },
  postComments(
    assetID: number,
    data: { note: string; userIds: number[]; markdownText: string },
  ) {
    return makeCancellableAPIRequest<CommentResponse>(
      "POST",
      `api/v2/assets/${assetID}/comments`,
      {
        data,
      },
    );
  },
  updateComments(
    assetID: number,
    data: {
      note: string;
      userIds: number[];
      markdownText: string;
      commentId: number;
    },
  ) {
    return makeCancellableAPIRequest<CommentResponse>(
      "PUT",
      `api/v2/assets/${assetID}/comments/${data.commentId}`,
      {
        data,
      },
    );
  },
  uploadManualAttachment(assetID: number, data: { fileName: string }) {
    return makeCancellableAPIRequest<{ id: number; signedUrl: string }>(
      "POST",
      `api/v2/assets/${assetID}/comments/attachment`,
      {
        data,
      },
    );
  },
  bulkUpdateAssets(data: {
    ids: string[];
    type: string;
    active?: boolean;
    tags?: string[];
    note: string;
  }) {
    return makeCancellableAPIRequest("PUT", `/api/v2/assets/bulk`, {
      data,
    });
  },
  addAssetManually(data: Partial<AssetEsDto>) {
    return makeCancellableAPIRequest<AssetsResDto>(
      "POST",
      `/api/v2/assets/upload/single`,
      {
        data,
      },
    );
  },
  importCSV(data: {
    toolName: string;
    assetType: string;
    delimiter: string;
    fileName: string;
    uploadTimezone: string;
  }) {
    const uploadTimezone = moment.tz.guess();
    return makeCancellableAPIRequest<{ signedUrl: string }>(
      "POST",
      "/api/v2/assets/upload",
      {
        data: {
          ...data,
          uploadTimezone,
        },
      },
    );
  },
  exportCSVFile(
    queryParams = {},
  ): CancellableAPIRequest<BaseResponseDto<AssetExportData>> {
    return makeCancellableAPIRequest("POST", "api/asset/download?format=CSV", {
      data: queryParams,
    });
  },

  getAssetTimelineStats(data: assetTimeLineReqDto) {
    return makeCancellableAPIRequest("POST", "/api/v2/assets/stats/timeline", {
      data,
    });
  },
  getAssetStatsAll(data: AssetStatsFilterRequest) {
    return makeCancellableAPIRequest<AssetStatsResponse>(
      "POST",
      "/api/v2/assets/assetStats/all",
      {
        data,
      },
    );
  },
  getAQLMetadata() {
    return makeCancellableAPIRequest<BaseResponseDto<AQLMetadataDTO>>(
      "GET",
      "/api/aql/metadata/ASSETS",
    );
  },
};

export default AssetsAPI;
